import { Fragment, useState } from "react";
import { Box, Button, Dialog, DialogContent, IconButton, Stack, Typography, alpha, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import CodeIcon from "@mui/icons-material/Code";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";

interface SddStage {
    title: string;
    subtitle: string;
    items: string[];
    importance: string;
    icon: React.ReactNode;
}

export const SDD_STAGES: SddStage[] = [
    {
        title: "Specify",
        subtitle: "Frame the problem before writing code.",
        items: [
            "Capture the problem statement and the users affected",
            "Define goals and explicit non-goals",
            "Draft acceptance criteria",
            "Flag unclear questions for review"
        ],
        importance: "A clear specification prevents wasted implementation work and gives reviewers a shared basis for judging whether a solution is correct.",
        icon: <DescriptionOutlinedIcon />
    },
    {
        title: "Plan",
        subtitle: "Decide the shape of the solution.",
        items: [
            "Choose architecture decisions and affected modules",
            "Define the data model and API contracts",
            "Identify migrations and security concerns",
            "Set the test strategy"
        ],
        importance: "Planning surfaces risk and technical tradeoffs before code is written, when changes are cheapest to make.",
        icon: <GridViewOutlinedIcon />
    },
    {
        title: "Define Tasks",
        subtitle: "Turn the plan into small, ordered steps.",
        items: [
            "Split work into small tasks and stories",
            "Sequence stories and tasks by dependency",
            "List files likely to change",
            "Set validation per story/task and human review checkpoints"
        ],
        importance: "Small, ordered stories and tasks keep each change reviewable and let drift be caught early instead of at the end.",
        icon: <ViewListOutlinedIcon />
    },
    {
        title: "Implement",
        subtitle: "Develop code one story and task at a time.",
        items: [
            "Work one story/task at a time with a context reset between them",
            "Apply the constraints set in planning",
            "Update the plan when reality differs from it"
        ],
        importance: "Isolating stories and tasks keeps generated code traceable to a single, reviewed intent rather than a sprawling, unverifiable change.",
        icon: <CodeIcon />
    },
    {
        title: "Validate",
        subtitle: "Confirm the work meets the spec.",
        items: ["Run tests, lint, and type checks", "Re-check acceptance criteria", "Manual review of the change", "Produce a spec-to-code diff"],
        importance: "Validation is where drift between the spec and the implementation is caught — the trigger for looping back to Specify.",
        icon: <TaskAltOutlinedIcon />
    }
];

const sectionLabelSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase" as const,
    color: "text.secondary",
    mb: 1
};

const SddWorkflow = () => {
    const theme = useTheme();
    const [activeStageIndex, setActiveStageIndex] = useState<number | null>(null);
    const activeStage = activeStageIndex === null ? null : SDD_STAGES[activeStageIndex];

    return (
        <>
            <Box component="section" aria-labelledby="sdd-workflow-title" sx={{ minWidth: 0 }}>
                <Typography
                    id="sdd-workflow-title"
                    sx={{
                        mb: 1.5,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.07em",
                        textTransform: "uppercase",
                        color: "text.secondary"
                    }}
                >
                    Specification Driven Development
                </Typography>

                <Box sx={{ width: "100%", overflowX: "auto", pb: 0.5 }}>
                    <Box sx={{ position: "relative", width: "100%", minWidth: 620, pb: 4 }}>
                        <Stack direction="row" alignItems="stretch">
                            {SDD_STAGES.map((stage, index) => (
                                <Fragment key={stage.title}>
                                    <Box
                                        component="button"
                                        type="button"
                                        aria-label={`View ${stage.title} stage details`}
                                        onClick={() => setActiveStageIndex(index)}
                                        sx={{
                                            flex: "1 1 0",
                                            minWidth: 0,
                                            minHeight: 74,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                            gap: 0.75,
                                            p: "11px 9px",
                                            borderRadius: 1.125,
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: "background.paper",
                                            color: "text.secondary",
                                            font: "inherit",
                                            textAlign: "left",
                                            cursor: "pointer",
                                            transition: "border-color 100ms ease, background-color 100ms ease, box-shadow 100ms ease",
                                            "&:hover": {
                                                borderColor: alpha(theme.palette.primary.main, 0.52),
                                                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`
                                            },
                                            "&:focus-visible": {
                                                outline: `2px solid ${alpha(theme.palette.primary.main, 0.5)}`,
                                                outlineOffset: 2
                                            }
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={0.75}>
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 19,
                                                    height: 19,
                                                    flex: "0 0 auto",
                                                    borderRadius: "50%",
                                                    backgroundColor: theme.palette.grey[100],
                                                    color: theme.palette.text.secondary,
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center"
                                                }}
                                            >
                                                {index + 1}
                                            </Box>
                                            <Box component="span" aria-hidden="true" sx={{ display: "inline-flex", "& svg": { fontSize: 13 } }}>
                                                {stage.icon}
                                            </Box>
                                        </Stack>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.primary", lineHeight: 1.25 }}>{stage.title}</Typography>
                                    </Box>
                                    {index < SDD_STAGES.length - 1 && (
                                        <Box
                                            aria-hidden="true"
                                            sx={{ width: 16, flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}
                                        >
                                            <ArrowForwardIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                                        </Box>
                                    )}
                                </Fragment>
                            ))}
                            <Stack aria-hidden="true" direction="row" alignItems="center" spacing={0.5} sx={{ flex: "0 0 auto", ml: 0.5 }}>
                                <ArrowForwardIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: "success.main" }}>Done</Typography>
                            </Stack>
                        </Stack>

                        <Box
                            component="svg"
                            viewBox="0 0 1000 30"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                            sx={{ position: "absolute", left: 0, bottom: 0, width: "100%", height: 30, color: "warning.main", pointerEvents: "none" }}
                        >
                            <path d="M835 4 L835 22 L82 22 L82 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 4" />
                            <path d="M76 4 L82 8 L88 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </Box>
                        <Typography
                            aria-hidden="true"
                            sx={{
                                position: "absolute",
                                left: "45.85%",
                                bottom: 0,
                                transform: "translateX(-50%)",
                                fontSize: 9.5,
                                fontWeight: 600,
                                color: "warning.main",
                                backgroundColor: "background.paper",
                                border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                                px: 0.875,
                                py: 0.125,
                                borderRadius: 10,
                                whiteSpace: "nowrap"
                            }}
                        >
                            drift found
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Dialog
                open={activeStage !== null}
                onClose={() => setActiveStageIndex(null)}
                fullWidth
                maxWidth="sm"
                transitionDuration={{ enter: 120, exit: 90 }}
                aria-labelledby="sdd-stage-title"
                PaperProps={{ sx: { borderRadius: 1.75, border: `1px solid ${theme.palette.divider}`, boxShadow: "0 30px 70px rgba(15, 23, 42, 0.25)" } }}
            >
                {activeStage && activeStageIndex !== null && (
                    <DialogContent sx={{ p: { xs: 2.5, sm: "26px 28px" } }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <Box
                                    sx={{
                                        width: 38,
                                        height: 38,
                                        flex: "0 0 auto",
                                        borderRadius: 1.25,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                        color: "primary.main",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        "& svg": { fontSize: 18 }
                                    }}
                                >
                                    {activeStage.icon}
                                </Box>
                                <Box>
                                    <Typography id="sdd-stage-title" sx={{ fontSize: 16, fontWeight: 600, color: "text.primary" }}>
                                        {activeStageIndex + 1}. {activeStage.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{activeStage.subtitle}</Typography>
                                </Box>
                            </Stack>
                            <IconButton
                                aria-label="Close SDD stage details"
                                size="small"
                                onClick={() => setActiveStageIndex(null)}
                                sx={{ width: 28, height: 28, backgroundColor: theme.palette.grey[100], color: "text.secondary" }}
                            >
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Stack>

                        <Typography sx={sectionLabelSx}>What happens</Typography>
                        <Box
                            component="ul"
                            sx={{
                                listStyleType: "disc",
                                listStylePosition: "outside",
                                fontSize: 13.5,
                                lineHeight: 1.75,
                                color: "text.secondary",
                                mt: 0,
                                mb: 2.25,
                                pl: 2.5,
                                "& > li": { display: "list-item", pl: 0.25 }
                            }}
                        >
                            {activeStage.items.map(item => (
                                <li key={item}>{item}</li>
                            ))}
                        </Box>
                        <Typography sx={sectionLabelSx}>Why it matters</Typography>
                        <Typography sx={{ fontSize: 13.5, lineHeight: 1.75, color: "text.secondary", mb: 2.5 }}>{activeStage.importance}</Typography>

                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ borderTop: `1px solid ${theme.palette.divider}`, pt: 2 }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ArrowBackIcon />}
                                disabled={activeStageIndex === 0}
                                onClick={() => setActiveStageIndex(index => (index === null ? null : Math.max(0, index - 1)))}
                                sx={{ minHeight: 34, textTransform: "none", fontSize: 12.5, fontWeight: 600 }}
                            >
                                Back
                            </Button>
                            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                                {activeStageIndex + 1} of {SDD_STAGES.length}
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                disabled={activeStageIndex === SDD_STAGES.length - 1}
                                onClick={() => setActiveStageIndex(index => (index === null ? null : Math.min(SDD_STAGES.length - 1, index + 1)))}
                                sx={{ minHeight: 34, textTransform: "none", fontSize: 12.5, fontWeight: 600 }}
                            >
                                Next
                            </Button>
                        </Stack>
                    </DialogContent>
                )}
            </Dialog>
        </>
    );
};

export default SddWorkflow;
